/*
  WI Tabbed Collection — Skeleton Framework Native
  File: assets/wi-tabbed-collection.js
*/

(() => {
  'use strict';

  if (window.__wiTabbedCollectionLoaded) return;
  window.__wiTabbedCollectionLoaded = true;

  const ROOT_SELECTOR = '[data-wi-tabbed-collection]';
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const createCarousel = (carousel, signal) => {
    const track = carousel?.querySelector('[data-wi-carousel-track]');
    if (!track) return null;

    const previous = carousel.querySelector('[data-wi-carousel-previous]');
    const next = carousel.querySelector('[data-wi-carousel-next]');
    const controls = carousel.querySelector('[data-wi-carousel-controls]');
    const progress = carousel.querySelector('[data-wi-carousel-progress]');
    const progressBar = carousel.querySelector('[data-wi-carousel-progress-bar]');

    let frame = 0;

    const slides = () =>
      Array.from(track.querySelectorAll('[data-wi-carousel-slide]'));

    const isRtl = () => document.documentElement.dir === 'rtl';

    const getMetrics = () => {
      const items = slides();
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      return { items, max };
    };

    const update = () => {
      frame = 0;
      const { max } = getMetrics();
      const left = Math.abs(track.scrollLeft);
      const hasOverflow = max > 2;

      if (controls) controls.hidden = !hasOverflow;
      if (previous) previous.disabled = !hasOverflow || left <= 6;
      if (next) next.disabled = !hasOverflow || left >= max - 6;

      const value = max > 0 ? Math.min(100, Math.round((left / max) * 100)) : 100;

      progress?.setAttribute('aria-valuenow', String(value));
      if (progressBar) progressBar.style.transform = `scaleX(${value / 100})`;
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    const move = (direction) => {
      const { items, max } = getMetrics();
      if (!items.length || max <= 0) return;

      const trackRect = track.getBoundingClientRect();
      const effectiveDirection = isRtl() ? -direction : direction;
      const paddingLeft = parseFloat(window.getComputedStyle(track).scrollPaddingLeft) || 16;

      let targetSlide = null;

      if (effectiveDirection > 0) {
        targetSlide = items.find((slide) => {
          const slideRect = slide.getBoundingClientRect();
          return slideRect.left - trackRect.left > paddingLeft + 12;
        });
      } else {
        targetSlide = [...items].reverse().find((slide) => {
          const slideRect = slide.getBoundingClientRect();
          return slideRect.left - trackRect.left < paddingLeft - 12;
        });
      }

      if (targetSlide) {
        const slideRect = targetSlide.getBoundingClientRect();
        const delta = slideRect.left - trackRect.left - paddingLeft;

        track.scrollBy({
          left: delta,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      } else {
        const step = (items[0]?.getBoundingClientRect().width || 280) + 16;
        track.scrollBy({
          left: effectiveDirection * step,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
    };

    previous?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });

    track.addEventListener('scroll', requestUpdate, {
      passive: true,
      signal
    });

    return {
      update,
      requestUpdate,
      destroy() {
        if (frame) cancelAnimationFrame(frame);
      }
    };
  };

  const initRoot = (root) => {
    if (!root?.matches(ROOT_SELECTOR)) return;

    const id = root.dataset.wiTabbedCollection;
    if (!id) return;

    instances.get(id)?.destroy();

    const controller = new AbortController();
    const { signal } = controller;

    const tabsContainer = root.querySelector('.wi-tabbed-collection__tabs');
    const tabs = Array.from(root.querySelectorAll('[data-wi-tab-button]'));
    const panels = Array.from(root.querySelectorAll('[data-wi-tab-panel]'));
    const panelMap = new Map(
      panels.map((panel) => [panel.dataset.wiTabPanel, panel])
    );

    const carouselMap = new Map();
    panels.forEach((panel) => {
      const carousel = panel.querySelector('[data-wi-carousel]');
      carouselMap.set(panel, createCarousel(carousel, signal));
    });

    const refreshPanel = (panel) => {
      if (!panel || panel.hidden) return;
      requestAnimationFrame(() => carouselMap.get(panel)?.update());
    };

    const activate = (tab, focus = false) => {
      if (!tab) return;

      const blockId = tab.dataset.blockId;
      const activePanel = panelMap.get(blockId);
      if (!activePanel) return;

      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        const active = panel === activePanel;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      if (focus) tab.focus({ preventScroll: true });

      if (tabsContainer) {
        const tabOffset = tab.offsetLeft - tabsContainer.clientWidth / 2 + tab.clientWidth / 2;
        tabsContainer.scrollTo({
          left: Math.max(0, tabOffset),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }

      refreshPanel(activePanel);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab), { signal });

      tab.addEventListener(
        'keydown',
        (event) => {
          let nextIndex = -1;

          if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % tabs.length;
          } else if (event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + tabs.length) % tabs.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = tabs.length - 1;
          }

          if (nextIndex < 0) return;
          event.preventDefault();
          activate(tabs[nextIndex], true);
        },
        { signal }
      );
    });

    root.addEventListener(
      'shopify:block:select',
      (event) => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;

        activate(
          root.querySelector(
            `[data-wi-tab-button][data-block-id="${CSS.escape(blockId)}"]`
          )
        );
      },
      { signal }
    );

    let resizeObserver = null;
    let lastWidth = Math.round(root.getBoundingClientRect().width);

    const refreshActive = () => {
      panels.filter((panel) => !panel.hidden).forEach(refreshPanel);
    };

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        const nextWidth = Math.round(root.getBoundingClientRect().width);
        if (nextWidth === lastWidth) return;
        lastWidth = nextWidth;
        refreshActive();
      });
      resizeObserver.observe(root);
    } else {
      window.addEventListener('resize', refreshActive, {
        passive: true,
        signal
      });
    }

    const initialTab =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
      tabs[0];

    if (initialTab) {
      const initialPanel = panelMap.get(initialTab.dataset.blockId);

      tabs.forEach((tab) => {
        const active = tab === initialTab;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        const active = panel === initialPanel;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    }

    requestAnimationFrame(refreshActive);

    const destroy = () => {
      controller.abort();
      resizeObserver?.disconnect();
      carouselMap.forEach((carousel) => carousel?.destroy());
      if (instances.get(id)?.root === root) instances.delete(id);
    };

    instances.set(id, { root, destroy });
  };

  const boot = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance) => {
      if (instance.root === scope || scope.contains?.(instance.root)) {
        instance.destroy();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), {
      once: true
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });
})();